import React, { useEffect, useState, Children } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Users, Clock, Star } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
const CountUp = ({
  end,
  duration = 2,
  suffix = ''
}: {
  end: number;
  duration?: number;
  suffix?: string;
}) => {
  const [count, setCount] = useState(0);
  const {
    ref,
    inView
  } = useInView({
    triggerOnce: true,
    threshold: 0.5
  });
  useEffect(() => {
    if (inView) {
      let startTime: number;
      let animationFrame: number;
      const animate = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const progress = timestamp - startTime;
        const percentage = Math.min(progress / (duration * 1000), 1);
        const easeOut = percentage === 1 ? 1 : 1 - Math.pow(2, -10 * percentage);
        setCount(Math.floor(end * easeOut));
        if (percentage < 1) {
          animationFrame = requestAnimationFrame(animate);
        } else {
          setCount(end);
        }
      };
      animationFrame = requestAnimationFrame(animate);
      return () => cancelAnimationFrame(animationFrame);
    }
  }, [inView, end, duration]);
  const displayCount = end % 1 !== 0 ? count.toFixed(1) : count;
  return <span ref={ref} data-id="element-396">
      {displayCount}
      {suffix}
    </span>;
};
export function SocialProof() {
  const {
    t
  } = useLanguage();
  const controls = useAnimation();
  const {
    ref,
    inView
  } = useInView({
    triggerOnce: true,
    threshold: 0.2
  });
  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.2
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0
    }
  };
  return <section className="py-12 bg-brand-darker border-y border-white/5 relative z-20" data-id="element-397">
      <div className="container mx-auto px-6" data-id="element-398">
        <motion.div ref={ref} variants={containerVariants} initial="hidden" animate={controls} className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-4 divide-y md:divide-y-0 md:divide-x divide-white/10" data-id="element-399">
          <motion.div variants={itemVariants} className="flex flex-col items-center justify-center text-center pt-8 md:pt-0 pb-8 md:pb-0" data-id="element-400">
            <div className="bg-brand-cyan/10 p-3 rounded-full mb-4" data-id="element-401">
              <Users className="w-6 h-6 text-brand-cyan" data-id="element-402" />
            </div>
            <h3 className="text-4xl font-bold text-white mb-2" data-id="element-403">
              +<CountUp end={127} data-id="element-404" />
            </h3>
            <p className="text-gray-400 font-medium uppercase tracking-wider text-sm" data-id="element-405">
              {t('social.clients')}
            </p>
          </motion.div>

          <motion.div variants={itemVariants} className="flex flex-col items-center justify-center text-center py-8 md:py-0" data-id="element-406">
            <div className="bg-brand-purple/10 p-3 rounded-full mb-4" data-id="element-407">
              <Clock className="w-6 h-6 text-brand-purple" data-id="element-408" />
            </div>
            <h3 className="text-4xl font-bold text-white mb-2" data-id="element-409">
              -<CountUp end={48} data-id="element-410" />h
            </h3>
            <p className="text-gray-400 font-medium uppercase tracking-wider text-sm" data-id="element-411">
              {t('social.delivery')}
            </p>
          </motion.div>

          <motion.div variants={itemVariants} className="flex flex-col items-center justify-center text-center pt-8 md:pt-0" data-id="element-412">
            <div className="bg-brand-cta/10 p-3 rounded-full mb-4" data-id="element-413">
              <Star className="w-6 h-6 text-brand-cta" data-id="element-414" />
            </div>
            <h3 className="text-4xl font-bold text-white mb-2" data-id="element-415">
              <CountUp end={4.9} duration={2} data-id="element-416" />
              /5
            </h3>
            <p className="text-gray-400 font-medium uppercase tracking-wider text-sm" data-id="element-417">
              {t('social.rating')}
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}